/**
 * Link-copy buttons for a release page's section headings and items
 * (spec-feature.md §3.3 "セクション・項目へのリンクコピー").
 *
 * internal/site/templates/release_markdown.go renders each Section heading
 * as `.release-heading[id]` and each Item as `.release-item[id]`, where `id`
 * is the anchor (GodevAnchor / item_id) already used for in-page linking.
 * This module injects a small button into every such element that, on
 * click, copies `location.origin + location.pathname + '#' + id` to the
 * clipboard and shows a brief checkmark confirmation.
 *
 * The button is added purely in JS (progressive enhancement): with no JS,
 * the elements render exactly as before and no copy affordance appears.
 * Styling is UnoCSS utility shortcuts (`copy-link-button`,
 * `copy-link-button--heading`, `copy-link-button--item`,
 * `copy-link-button--copied`; see uno.config.mjs) - UnoCSS's build:css
 * script scans this file directly (package.json), so these class names must
 * appear as complete literal substrings here (see VARIANT_CLASS below).
 */

const HEADING_SELECTOR = '.release-heading[id]';
const ITEM_SELECTOR = '.release-item[id]';
const BUTTON_CLASS = 'copy-link-button';
const COPIED_CLASS = 'copy-link-button--copied';

/** How long the checkmark confirmation stays visible after a successful copy. */
const FEEDBACK_DURATION_MS = 1500;

// The 0.9rem icon size isn't a default Tailwind/UnoCSS size step, so it's
// pinned via arbitrary-value utilities directly on the <svg> markup (the
// `copy-link-button` shortcut only sizes the button itself).
const LINK_ICON =
  '<svg class="w-[0.9rem] h-[0.9rem]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m6.656-2.828l1.5-1.5a4 4 0 115.656 5.656l-3 3a4 4 0 01-5.656 0"></path></svg>';

const CHECK_ICON =
  '<svg class="w-[0.9rem] h-[0.9rem]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path></svg>';

/** Builds the shareable URL for anchor id on the current page. */
function anchorURL(id: string): string {
  return `${window.location.origin}${window.location.pathname}#${id}`;
}

/**
 * Copies url to the clipboard and, on success, briefly swaps button's icon
 * to a checkmark. Exported for testing.
 */
export async function copyLinkAndShowFeedback(url: string, button: HTMLButtonElement): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
  } catch (err) {
    console.error('copy-link: failed to write to clipboard', err);
    return;
  }

  const original = button.innerHTML;
  button.innerHTML = CHECK_ICON;
  button.classList.add(COPIED_CLASS);
  setTimeout(() => {
    button.innerHTML = original;
    button.classList.remove(COPIED_CLASS);
  }, FEEDBACK_DURATION_MS);
}

// Maps each variant to its complete UnoCSS shortcut class name (uno.config.mjs),
// rather than building it via template-literal concatenation
// (`${BUTTON_CLASS}--${variant}`): UnoCSS's static extractor can only
// discover a shortcut name that appears as a complete, contiguous literal
// substring somewhere in a scanned file - a concatenated version would
// never contain the literal strings 'copy-link-button--heading' /
// 'copy-link-button--item' anywhere in this file's source text, so neither
// class's styling would be generated (see release_markdown.go's
// releaseItemAccentClass for the same pitfall on the Go side).
const VARIANT_CLASS: Record<'heading' | 'item', string> = {
  heading: 'copy-link-button--heading',
  item: 'copy-link-button--item',
};

function createCopyButton(id: string, variant: 'heading' | 'item', label: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `${BUTTON_CLASS} ${VARIANT_CLASS[variant]}`;
  button.setAttribute('aria-label', label);
  button.innerHTML = LINK_ICON;
  button.addEventListener('click', (event) => {
    // Don't let the click bubble into the enclosing .release-item's
    // hover/click handling (e.g. touch-toggle in index.ts), and don't
    // trigger any default anchor/button navigation.
    event.preventDefault();
    event.stopPropagation();
    void copyLinkAndShowFeedback(anchorURL(id), button);
  });
  return button;
}

/**
 * Injects a copy-link button into every `.release-heading[id]` and
 * `.release-item[id]` on the page that doesn't already have one. Safe to
 * call multiple times. Exported for testing.
 */
export function initializeCopyLinkButtons(): void {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return;

  document.querySelectorAll<HTMLElement>(HEADING_SELECTOR).forEach((heading) => {
    if (heading.querySelector(`:scope > .${BUTTON_CLASS}`)) return;
    heading.appendChild(createCopyButton(heading.id, 'heading', 'この見出しへのリンクをコピー'));
  });

  document.querySelectorAll<HTMLElement>(ITEM_SELECTOR).forEach((item) => {
    if (item.querySelector(`:scope > .${BUTTON_CLASS}`)) return;
    item.appendChild(createCopyButton(item.id, 'item', 'この項目へのリンクをコピー'));
  });
}
